import { Calendar } from 'lucide-react';
import { useMemo } from 'react';

import { convertStructuredFilterOperatorToFilterOperator } from '@/hooks/domain/App/useStructuredFilter';
import { getFilterOperatorText } from '@/types/dataTableConst';
import { FEEDBACK_COLUMNS } from '@/types/filterKeyConst';
import { DATE_FORMAT, getTimezoneDayjs } from '@/utils/time';

import useStructuredFilterMap from '../configs/useStructuredFilterMap';
import structuredFilterUtils from '../utils/structuredFilter.utils';

export const StaticFilterViewer = (props: {
  formats: StructuredFilterQueryFormatT[];
}) => {
  const { dateRange, filterQueryFormats } = useMemo(() => {
    if (props.formats.length === 0)
      return { dateRange: null, filterQueryFormats: [] };
    let filterQueryFormats = [...props.formats];

    const startDateIndex = filterQueryFormats.findIndex(
      (filter) => filter.columnKey === FEEDBACK_COLUMNS.DATE_START,
    );
    const startDate = filterQueryFormats[startDateIndex];

    filterQueryFormats.splice(startDateIndex, 1);

    const endDateIndex = filterQueryFormats.findIndex(
      (filter) => filter.columnKey === FEEDBACK_COLUMNS.DATE_END,
    );
    const endDate = filterQueryFormats[endDateIndex];

    filterQueryFormats.splice(endDateIndex, 1);

    return {
      dateRange: {
        startDate: startDate.filterValues[0].filterName,
        endDate: endDate.filterValues[0].filterName,
      },
      filterQueryFormats,
    };
  }, [props.formats]);

  if (props.formats.length === 0) return null;

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {dateRange && (
        <span className="inline-flex items-center gap-2 border text-xs font-medium text-foreground rounded-full pl-2.5 pr-2 h-6">
          <Calendar className="size-3" />
          {getTimezoneDayjs(dateRange.startDate).format(
            DATE_FORMAT.readable,
          )} -{' '}
          {getTimezoneDayjs(dateRange.endDate).format(DATE_FORMAT.readable)}
        </span>
      )}
      {filterQueryFormats.map((filter) => (
        <StaticFilterItem key={filter.columnKey} item={filter} />
      ))}
    </div>
  );
};

export const StaticFilterItem = (props: {
  item: StructuredFilterQueryFormatT;
}) => {
  const structuredFilterMap = useStructuredFilterMap();
  const { item } = props;
  const filterValuesLabel = item.filterValues
    .map((value) => value.filterName)
    .join(', ');
  const previewFilterValuesLabel =
    filterValuesLabel.length > 24
      ? `${filterValuesLabel.slice(0, 24)}...`
      : filterValuesLabel;

  const column = structuredFilterUtils.getFieldInfo(
    item.columnKey,
    structuredFilterMap,
  );
  if (!column) return null;

  return (
    <span className="inline-flex items-center gap-2 border text-xs font-medium text-foreground rounded-full py-[3px] px-3">
      <span className="shrink-0">{column.fieldInfo.columnName}</span>
      <span className="shrink-0 text-muted-foreground px-1">
        {getFilterOperatorText({
          operator: convertStructuredFilterOperatorToFilterOperator(
            item.operator,
          ),
          isMultiValueField: column.fieldInfo.isMultiValueField,
          multiFilter: item.filterValues.length > 1,
        })}
      </span>
      <span className="min-w-0 whitespace-nowrap" title={filterValuesLabel}>
        {previewFilterValuesLabel}
      </span>
    </span>
  );
};
